import { ArchiveIcon, EditIcon, RotateCcwIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextareaFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import type { Project } from '../data/databaseSchema';
import { handleApiError } from '../lib/errors';
import { type ProjectOptionItem, projectService } from '../services/projectService';
import { logger } from '../utils/logger';

function isoToDateInputValue(iso: string | undefined | null): string {
  if (!iso) return '';
  const s = String(iso).trim();
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') {
    return s.slice(0, 10);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function dateInputToIsoDateOnly(v: string): string {
  return v?.trim() ?? '';
}

interface ProjectFormData {
  project_number: string;
  name: string;
  description: string;
  type: string;
  portfolio: string;
  process_technology: string;
  delivery_model: string;
  start_date: string;
  end_date: string;
  status?: string;
}

interface ProjectDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Project) => void;
  onEntrySaved?: () => void;
  project?: Project | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onArchiveRequest?: () => void;
  onRestoreRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const ProjectDrawer: React.FC<ProjectDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  project,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onArchiveRequest,
  onRestoreRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    project_number: '',
    name: '',
    description: '',
    type: 'Internal',
    portfolio: '',
    process_technology: '',
    delivery_model: '',
    start_date: '',
    end_date: '',
  });

  const [options, setOptions] = useState<{
    portfolios: ProjectOptionItem[];
    processTechnologies: ProjectOptionItem[];
    deliveryModels: ProjectOptionItem[];
  }>({ portfolios: [], processTechnologies: [], deliveryModels: [] });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const portfolioOptions = useMemo(
    () => options.portfolios.map((opt) => ({ value: opt.display, label: opt.display })),
    [options.portfolios]
  );
  const processTechnologyOptions = useMemo(
    () => options.processTechnologies.map((opt) => ({ value: opt.display, label: opt.display })),
    [options.processTechnologies]
  );
  const deliveryModelOptions = useMemo(
    () => options.deliveryModels.map((opt) => ({ value: opt.display, label: opt.display })),
    [options.deliveryModels]
  );

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && project) {
        setFormData({
          project_number: project.project_number || '',
          name: project.name || '',
          description: project.description || '',
          type: project.type || '',
          portfolio: project.portfolio ?? '',
          process_technology: project.process_technology ?? '',
          delivery_model: project.delivery_model ?? '',
          start_date: isoToDateInputValue(project.start_date),
          end_date: isoToDateInputValue(project.end_date),
          status: project.status || 'active',
        });
      } else {
        setFormData({
          project_number: '',
          name: '',
          description: '',
          type: 'Internal',
          portfolio: '',
          process_technology: '',
          delivery_model: '',
          start_date: '',
          end_date: '',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, project]);

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([
      projectService.getPortfolios(),
      projectService.getProcessTechnologies(),
      projectService.getDeliveryModels(),
    ])
      .then(([portfolios, processTechnologies, deliveryModels]) => {
        setOptions({ portfolios, processTechnologies, deliveryModels });
      })
      .catch((err) => {
        logger.error('Failed to load project options', err);
      });
  }, [isOpen]);

  const handleInputChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.project_number || formData.project_number.trim() === '') {
      newErrors.project_number = 'Project number is required';
    }

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Project name is required';
    }

    if (!formData.type || formData.type.trim() === '') {
      newErrors.type = 'Project type is required';
    } else {
      const validTypes = ['Internal', 'External'];
      if (!validTypes.includes(formData.type)) {
        newErrors.type = 'Project type must be Internal or External';
      }
    }

    if (!formData.start_date?.trim()) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date?.trim()) {
      newErrors.end_date = 'End date is required';
    }
    if (formData.start_date?.trim() && formData.end_date?.trim()) {
      if (formData.end_date.trim() < formData.start_date.trim()) {
        newErrors.end_date = 'End date must be on or after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData = {
        project_number: formData.project_number.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type.trim() || undefined,
        portfolio: formData.portfolio.trim() || undefined,
        process_technology: formData.process_technology.trim() || undefined,
        delivery_model: formData.delivery_model.trim() || undefined,
        start_date: dateInputToIsoDateOnly(formData.start_date),
        end_date: dateInputToIsoDateOnly(formData.end_date),
        ...(isEdit && formData.status ? { status: formData.status } : {}),
      };

      const response =
        isEdit && project
          ? await projectService.updateProject(project.id, apiData)
          : await projectService.createProject(apiData);

      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving project:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Project' : isEdit ? 'Edit Project' : 'Add Project'}
      description={
        readOnly
          ? 'Project details.'
          : isEdit
            ? 'Update the project details below.'
            : 'Fill in the details for the new project.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Project' : 'Add Project'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onRestoreRequest ? (
            <Button type="button" size="sm" variant="success" onClick={onRestoreRequest}>
              <RotateCcwIcon data-icon="inline-start" />
              Restore
            </Button>
          ) : null}
          {onArchiveRequest ? (
            <Button type="button" size="sm" variant="secondary" onClick={onArchiveRequest}>
              <ArchiveIcon data-icon="inline-start" />
              Archive
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="Project Number"
          required
          value={formData.project_number}
          onChange={(v) => handleInputChange('project_number', v)}
          error={errors.project_number}
          disabled={isEdit || fieldsDisabled}
          placeholder="Enter project number"
          copyable={readOnly}
        />
        <TextFormField
          label="Project Name"
          required
          value={formData.name}
          onChange={(v) => handleInputChange('name', v)}
          error={errors.name}
          disabled={fieldsDisabled}
          placeholder="Enter project name"
          copyable={readOnly}
        />
        <TextareaFormField
          label="Description"
          value={formData.description}
          onChange={(v) => handleInputChange('description', v)}
          placeholder="Enter project description"
          disabled={fieldsDisabled}
        />
        <SelectFormField
          label="Type"
          required
          value={formData.type}
          onChange={(v) => handleInputChange('type', v)}
          options={[
            { value: 'Internal', label: 'Internal' },
            { value: 'External', label: 'External' },
          ]}
          placeholder="Select a project type"
          error={errors.type}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <SelectFormField
          label="Portfolio"
          value={formData.portfolio}
          onChange={(v) => handleInputChange('portfolio', v)}
          options={portfolioOptions}
          placeholder="Select portfolio"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <SelectFormField
          label="Process Technology"
          value={formData.process_technology}
          onChange={(v) => handleInputChange('process_technology', v)}
          options={processTechnologyOptions}
          placeholder="Select process technology"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <SelectFormField
          label="Delivery Model"
          value={formData.delivery_model}
          onChange={(v) => handleInputChange('delivery_model', v)}
          options={deliveryModelOptions}
          placeholder="Select delivery model"
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="Start date"
          type="date"
          required
          value={formData.start_date}
          onChange={(v) => handleInputChange('start_date', v)}
          error={errors.start_date}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="End date"
          type="date"
          required
          value={formData.end_date}
          onChange={(v) => handleInputChange('end_date', v)}
          error={errors.end_date}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
